import type { Boot, FileRef, Follow, Message, NotifyLevel, Pin, Room, RoomState, Saved, Scheduled, Task, TaskEvent, User } from "../../server/src/types";

export type { Boot, FileRef, Follow, Message, NotifyLevel, Pin, Room, RoomState, Saved, Scheduled, Task, TaskEvent, User };
export type { LinkPreview } from "../../server/src/types";
export type { Presence, Push, Repeat, TaskStatus } from "../../server/src/types";

export class ApiError extends Error {
  constructor(public status: number, message: string) { super(message); }
}

async function call<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(path, {
    method,
    credentials: "same-origin",
    headers: { "x-hub": "1", ...(body !== undefined ? { "content-type": "application/json" } : {}) },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    let msg = res.statusText;
    try { msg = (await res.json()).error ?? msg; } catch {}
    throw new ApiError(res.status, msg);
  }
  return res.json();
}

export interface SyncResult {
  messages: Message[]; tasks: Task[]; events: TaskEvent[]; rooms: Room[]; states: RoomState[]; users: User[]; serverTime: string;
}
export interface SearchResult {
  messages: Message[]; tasks: Task[];
  files: { id: string; name: string; mime: string; size: number; messageId: number; roomId: number; userId: number; createdAt: string }[];
}
export interface ThreadItem { follow: Follow; root: Message; replies: Message[] }
export interface TaskDetail { task: Task; events: TaskEvent[]; source: Message | null }

export const api = {
  boot: () => call<Boot>("GET", "/api/boot"),
  sync: (since: string) => call<SyncResult>("GET", `/api/sync?since=${encodeURIComponent(since)}`),
  page: (roomId: number, before?: number) => call<Message[]>("GET", `/api/rooms/${roomId}/messages${before ? `?before=${before}` : ""}`),
  read: (roomId: number, messageId: number) => call<{ lastReadId: number }>("POST", `/api/rooms/${roomId}/read`, { messageId }),
  dm: (userId: number) => call<Room>("POST", "/api/dms", { userId }),
  group: (name: string, memberIds: number[]) => call<Room>("POST", "/api/groups", { name, memberIds }),
  editGroup: (id: number, p: { name?: string; add?: number[]; remove?: number[] }) => call<Room>("PATCH", `/api/groups/${id}`, p),
  send: (p: { roomId: number; body: string; parentId?: number; alsoInChat?: boolean; clientId: string; fileIds?: string[] }) => call<Message>("POST", "/api/messages", p),
  edit: (id: number, body: string) => call<Message>("PATCH", `/api/messages/${id}`, { body }),
  del: (id: number) => call<{ ok: true }>("DELETE", `/api/messages/${id}`),
  react: (id: number, emoji: string) => call<Message>("POST", `/api/messages/${id}/reactions`, { emoji }),
  thread: (id: number) => call<{ root: Message; replies: Message[] }>("GET", `/api/messages/${id}/thread`),
  newTask: (p: { title: string; ownerId: number; due?: string | null; repeat?: string | null; roomId?: number | null; sourceMessageId?: number | null }) => call<Task>("POST", "/api/tasks", p),
  task: (id: number) => call<TaskDetail>("GET", `/api/tasks/${id}`),
  editTask: (id: number, p: Partial<{ status: string; title: string; due: string | null; repeat: string | null; ownerId: number }>) => call<Task>("PATCH", `/api/tasks/${id}`, p),
  replyTask: (id: number, body: string, fileIds?: string[]) => call<{ id: number }>("POST", `/api/tasks/${id}/replies`, { body, fileIds }),
  updateMe: (p: { status?: string | null; color?: string; avatarFileId?: string | null; notifyDefault?: string; sound?: string; volume?: number }) => call<User>("PATCH", "/api/me", p),
  addUser: (p: { email: string; name: string; color?: string }) => call<User>("POST", "/api/users", p),
  editUser: (id: number, p: { name?: string; color?: string; active?: boolean }) => call<User>("PATCH", `/api/users/${id}`, p),
  chat: (userIds: number[]) => call<Room>("POST", "/api/chats", { userIds }),
  setNotify: (roomId: number, level: NotifyLevel | "default") => call<RoomState>("PATCH", `/api/rooms/${roomId}/notify`, { level }),
  search: (q: string) => call<SearchResult>("GET", `/api/search?q=${encodeURIComponent(q)}`),
  mentions: () => call<Message[]>("GET", "/api/mentions"),
  seeMentions: (upTo: number) => call<{ id: number }>("POST", "/api/mentions/seen", { upTo }),
  threads: () => call<ThreadItem[]>("GET", "/api/threads"),
  readThread: (rootId: number, upTo: number) => call<Follow | null>("POST", `/api/threads/${rootId}/read`, { upTo }),
  follow: (rootId: number, on: boolean) => call<{ following: boolean }>("POST", `/api/threads/${rootId}/follow`, { on }),
  unread: (messageId: number) => call<{ roomId: number; rootId: number | null }>("POST", `/api/messages/${messageId}/unread`),
  save: (messageId: number, remindAt: string | null, on = true) => call<Saved[]>("POST", "/api/saved", { messageId, remindAt, on }),
  pin: (messageId: number, on: boolean) => call<Pin[]>("POST", "/api/pins", { messageId, on }),
  schedule: (p: { roomId: number; body: string; sendAt: string; parentId?: number | null; alsoInChat?: boolean; fileIds?: string[] }) => call<Scheduled[]>("POST", "/api/scheduled", p),
  editScheduled: (id: number, p: { body?: string; sendAt?: string; cancel?: boolean; now?: boolean }) => call<Scheduled[]>("PATCH", `/api/scheduled/${id}`, p),
  logout: () => call<{ ok: true }>("POST", "/auth/logout"),

  async upload(file: File, onProgress?: (f: number) => void): Promise<FileRef> {
    const start = await call<{ id: string; url: string; headers: Record<string, string> }>("POST", "/api/files", { name: file.name, mime: file.type || "application/octet-stream", size: file.size });
    await new Promise<void>((resolve, reject) => {
      const x = new XMLHttpRequest();
      x.open("PUT", start.url);
      for (const [k, v] of Object.entries(start.headers)) x.setRequestHeader(k, v);
      if (start.url.startsWith("/")) x.setRequestHeader("x-hub", "1");
      x.upload.onprogress = (e) => e.lengthComputable && onProgress?.(e.loaded / e.total);
      x.onload = () => (x.status < 300 ? resolve() : reject(new ApiError(x.status, "Upload failed")));
      x.onerror = () => reject(new ApiError(0, "Upload failed"));
      x.send(file);
    });
    return call<FileRef>("POST", `/api/files/${start.id}/done`);
  },
};

export const fileUrl = (id: string, download = false) => `/api/files/${id}${download ? "?dl=1" : ""}`;
